import { createMiddleware, createStart } from "@tanstack/react-start";
import { isPublicHtml } from "@/lib/public-html-paths";

// NOTE: We intentionally do NOT import the generated `attachSupabaseAuth`
// from `@/integrations/supabase/auth-attacher`. That helper does a top-level
// `import { supabase } from "./client"`, which drags the entire
// @supabase/supabase-js bundle (~50 KiB gzip) into every route's initial
// JS chunk, including public pages like `/` where no auth call ever runs
// (confirmed via PageSpeed "unused JavaScript" audit, Jul 20 2026).
//
// This local replacement is functionally identical, but the supabase client
// is dynamically imported inside the middleware handler. The chunk only
// downloads when a serverFn RPC actually fires (auth flows, admin,
// portal, agency, authed-mutation), keeping the public homepage lean.
//
// Do not swap this back to the generated attacher without a matching
// bundle-size regression check.
const attachSupabaseAuth = createMiddleware({ type: "function" }).client(
  async ({ next }) => {
    const { supabase } = await import("@/integrations/supabase/client");
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    return next({
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },
);

function classify(pathname: string): string | null {
  if (pathname.startsWith("/api/")) return null;
  if (
    pathname.startsWith("/_build/") ||
    pathname.startsWith("/assets/") ||
    pathname.startsWith("/fonts/")
  ) {
    return "public, max-age=31536000, immutable";
  }
  if (/\.(?:ico|png|jpg|jpeg|webp|avif|svg|gif|txt|xml|webmanifest|woff2?)$/i.test(pathname)) {
    return "public, max-age=604800";
  }
  if (/\.[a-z0-9]{2,5}$/i.test(pathname)) return null;
  if (!isPublicHtml(pathname)) return null;
  return "public, max-age=0, s-maxage=300, stale-while-revalidate=86400";
}

/**
 * Trailing-slash canonicalization: the framework answers trailing-slash
 * requests with a 307 Temporary Redirect when no route claims the variant.
 * Temporary redirects do not consolidate signals, so upgrade exactly that
 * response to a single-hop 301 — while leaving every explicit redirect
 * (legacy maps, server-handler index routes) untouched.
 */
const trailingSlashRedirectMiddleware = createMiddleware().server(
  async ({ next, request }) => {
    if (request.method !== "GET" && request.method !== "HEAD") return next();

    let pathname = "";
    let search = "";
    try {
      const url = new URL(request.url);
      pathname = url.pathname;
      search = url.search;
    } catch {
      return next();
    }

    if (pathname === "/" || !pathname.endsWith("/") || pathname.startsWith("/api/")) {
      return next();
    }

    const ctx = await next();
    const response = ctx?.response;
    if (!(response instanceof Response) || response.status !== 307) return ctx;

    const location = response.headers.get("Location");
    if (!location) return ctx;
    const stripped = pathname.replace(/\/+$/, "");
    try {
      if (new URL(location, request.url).pathname !== stripped) return ctx;
    } catch {
      return ctx;
    }

    return new Response(null, {
      status: 301,
      headers: { Location: `${stripped}${search}` },
    });
  },
);

const edgeCacheMiddleware = createMiddleware().server(
  async ({ next, request }) => {
    if (request.method !== "GET") return next();

    let pathname = "";
    try {
      pathname = new URL(request.url).pathname;
    } catch {
      return next();
    }

    const cacheControl = classify(pathname);
    const ctx = await next();

    if (cacheControl && ctx?.response instanceof Response) {
      try {
        ctx.response.headers.set("Cache-Control", cacheControl);
        // Make the shared-cache intent explicit for Cloudflare even if another
        // platform layer rewrites the browser-facing Cache-Control header.
        ctx.response.headers.set("CDN-Cache-Control", cacheControl);

        // Remove Vary: Cookie so Cloudflare edge cache key isn't fragmented
        // by per-visitor analytics cookies on public HTML.
        const vary = ctx.response.headers.get("Vary");
        if (vary && /cookie/i.test(vary)) {
          const cleaned = vary
            .split(",")
            .map((value) => value.trim())
            .filter((value) => value && !/^cookie$/i.test(value))
            .join(", ");
          if (cleaned) ctx.response.headers.set("Vary", cleaned);
          else ctx.response.headers.delete("Vary");
        }
      } catch {
        /* headers may be immutable on some Response subclasses, ignore */
      }
    }
    return ctx;
  },
);

export const startInstance = createStart(() => ({
  requestMiddleware: [trailingSlashRedirectMiddleware, edgeCacheMiddleware],
  functionMiddleware: [attachSupabaseAuth],
}));
